import { useState, useCallback, useEffect, useMemo } from "react";
import {
  useQuery,
  DocumentNode,
  QueryHookOptions,
  OperationVariables,
} from "@apollo/client";

interface PaginatedQueryOptions<TData, TVariables extends OperationVariables>
  extends Omit<QueryHookOptions<TData, TVariables>, "variables"> {
  variables: TVariables;
  pageSize?: number;
  extractItems: (data: TData | undefined) => any[];
  updateCacheWithNewItems: (prev: TData, newItems: any[]) => TData;
}

interface PaginatedQueryResult<TData> {
  data: TData | undefined;
  loading: boolean;
  error: any;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  refetchWithNewVariables: (newVariables: Partial<OperationVariables>) => void;
  reset: () => void;
}

export function usePaginatedQuery<TData, TVariables extends OperationVariables>(
  query: DocumentNode,
  options: PaginatedQueryOptions<TData, TVariables>
): PaginatedQueryResult<TData> {
  const {
    variables,
    pageSize = 10,
    extractItems,
    updateCacheWithNewItems,
    ...queryOptions
  } = options;

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const variablesHash = useMemo(() => {
    return JSON.stringify(variables);
  }, [variables]);

  const queryVariables = useMemo(
    () =>
      ({
        ...variables,
        first: pageSize,
        offset: 0,
      } as TVariables),
    [variables, pageSize]
  );

  const { data, loading, error, fetchMore, refetch } = useQuery<
    TData,
    TVariables
  >(query, {
    ...queryOptions,
    variables: queryVariables,
    onCompleted: (data) => {
      const items = extractItems(data);
      if (items.length < pageSize) {
        setHasMore(false);
      }
      if (queryOptions.onCompleted) {
        queryOptions.onCompleted(data);
      }
    },
  });

  useEffect(() => {
    setHasMore(true);
    setIsLoadingMore(false);
  }, [variablesHash]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    const currentItems = extractItems(data);
    const currentOffset = currentItems.length;

    fetchMore({
      variables: {
        ...variables,
        first: pageSize,
        offset: currentOffset,
      } as TVariables,
      updateQuery: (prev, { fetchMoreResult }) => {
        setIsLoadingMore(false);

        if (!fetchMoreResult) return prev;

        const newItems = extractItems(fetchMoreResult);

        // Check if we've reached the end
        if (newItems.length < pageSize) {
          setHasMore(false);
        }

        // Update cache with new items appended to existing ones
        return updateCacheWithNewItems(prev, newItems);
      },
    }).catch(() => {
      setIsLoadingMore(false);
    });
  }, [
    hasMore,
    isLoadingMore,
    data,
    fetchMore,
    variables,
    pageSize,
    extractItems,
    updateCacheWithNewItems,
  ]);

  const refetchWithNewVariables = useCallback(
    (newVariables: Partial<OperationVariables>) => {
      setHasMore(true);
      setIsLoadingMore(false);

      const updatedVariables = {
        ...variables,
        ...newVariables,
        first: pageSize,
        offset: 0,
      } as TVariables;

      refetch(updatedVariables);
    },
    [refetch, variables, pageSize]
  );

  const reset = useCallback(() => {
    setHasMore(true);
    setIsLoadingMore(false);
  }, []);

  return {
    data,
    loading,
    error,
    hasMore,
    isLoadingMore,
    loadMore,
    refetchWithNewVariables,
    reset,
  };
}
